import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  Loader2, CheckCircle2, ExternalLink, AlertCircle, RefreshCw, Trash2,
  Plus, Headphones, LifeBuoy, Ticket as TicketIcon, MessageSquare, X,
  Inbox, Check, Edit3, ShieldAlert, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

// ─── Provider catalog ──────────────────────────────────────────────────────────
// Visual + form-field metadata. Adding a 4th provider is now ~30 lines.
const PROVIDERS = [
  {
    key: 'zendesk',
    name: 'Zendesk',
    color: 'bg-emerald-600',
    description: 'Connect your Zendesk Support workspace',
    helpUrl: 'https://support.zendesk.com/hc/en-us/articles/4408889192858',
    helpSteps: [
      "Sign in to your Zendesk account as an Admin",
      "Click the Settings gear (top-right) → Apps and integrations",
      "Click 'Zendesk API' in the left sidebar",
      "Turn 'Token Access' ON, then click 'Add API token'",
      "Copy the token (it's only shown once!) and paste it below",
    ],
    fields: [
      { name: 'subdomain', label: 'Subdomain', placeholder: 'acme', hint: "Just the part before .zendesk.com" },
      { name: 'email', label: 'Your Zendesk login email', type: 'email', placeholder: 'you@yourcompany.com' },
      { name: 'api_token', label: 'API Token', type: 'password', placeholder: 'paste your token', hint: 'The token you just copied from Zendesk' },
    ],
  },
  {
    key: 'freshdesk',
    name: 'Freshdesk',
    color: 'bg-blue-600',
    description: 'Connect your Freshdesk helpdesk',
    helpUrl: 'https://support.freshdesk.com/en/support/solutions/articles/215517-how-to-find-your-api-key',
    helpSteps: [
      'Sign in to Freshdesk',
      'Click your profile avatar (top-right) → Profile Settings',
      "On the right side, click 'View API Key' (you may be asked for your password)",
      'Copy the API key shown',
      'Paste your portal domain and the API key below',
    ],
    fields: [
      { name: 'domain', label: 'Portal domain', placeholder: 'acme.freshdesk.com', hint: 'Your full Freshdesk URL minus https://' },
      { name: 'api_token', label: 'API Key', type: 'password', placeholder: 'paste your API key' },
    ],
  },
  {
    key: 'jira',
    name: 'Jira Service Management',
    color: 'bg-indigo-600',
    description: 'Connect your Atlassian Jira Service Management project',
    helpUrl: 'https://id.atlassian.com/manage-profile/security/api-tokens',
    helpSteps: [
      'Open id.atlassian.com → Security → API tokens',
      "Click 'Create API token', label it 'Harmonix', click Create",
      'Copy the token (you can only see it once)',
      'Paste your Jira site URL, your Atlassian login email, and the token below',
      "(Optional) If your project uses a key like 'SUPPORT', enter it to scope tickets to just that project",
    ],
    fields: [
      { name: 'site_url', label: 'Your Jira site URL', placeholder: 'https://acme.atlassian.net' },
      { name: 'email', label: 'Atlassian login email', type: 'email', placeholder: 'you@yourcompany.com' },
      { name: 'api_token', label: 'API Token', type: 'password', placeholder: 'paste your token' },
      { name: 'project_key', label: 'Project key (optional)', placeholder: 'SUPPORT', hint: 'Leave blank to read tickets from every project you can access' },
    ],
  },
];

const providerOf = (key) => PROVIDERS.find((p) => p.key === key) || PROVIDERS[0];

// ─── Tickets viewer (drawer) ───────────────────────────────────────────────────
const TicketsViewer = ({ chatbotId, connection, open, onClose }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('open');
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    if (!connection) return;
    setLoading(true);
    try {
      const r = await api.get(`/chatbots/${chatbotId}/support-portals/${connection.connection_id}/tickets`, { params: { status, limit: 25 } });
      setTickets(r.data?.tickets || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load tickets');
    } finally { setLoading(false); }
  }, [chatbotId, connection, status]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const submitNote = async () => {
    if (!noteText.trim() || !noteFor) return;
    setPosting(true);
    try {
      await api.post(`/chatbots/${chatbotId}/support-portals/${connection.connection_id}/tickets/${noteFor.id}/note`, { body: noteText });
      toast.success('Internal note posted');
      setNoteFor(null); setNoteText('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not post note');
    } finally { setPosting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TicketIcon className="w-5 h-5 text-blue-600" />
            Tickets from {connection?.label}
          </DialogTitle>
          <DialogDescription>
            Read-only view of open tickets in your support portal. You can post internal notes that only your team sees.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 pb-2 border-b">
          <div className="flex gap-1.5">
            {['open', 'pending', 'resolved'].map((s) => (
              <Button
                key={s}
                size="sm"
                variant={status === s ? 'default' : 'outline'}
                onClick={() => setStatus(s)}
                className={status === s ? 'bg-blue-600 hover:bg-blue-700' : ''}
                data-testid={`tickets-filter-${s}`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </Button>
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="tickets-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>

        <div className="overflow-y-auto -mx-6 px-6 flex-1">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <LifeBuoy className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              No {status} tickets found.
            </div>
          ) : (
            <div className="space-y-2 py-3">
              {tickets.map((t) => (
                <div key={t.id} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50" data-testid={`ticket-row-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-slate-500">#{t.id}</span>
                        <Badge variant="outline" className="text-xs">{t.status}</Badge>
                        {t.priority && t.priority !== 'normal' && (
                          <Badge className={t.priority === 'urgent' ? 'bg-red-100 text-red-700' : t.priority === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}>
                            {t.priority}
                          </Badge>
                        )}
                      </div>
                      <p className="font-medium text-slate-900 truncate">{t.subject}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {t.requester_name || t.requester_email || 'Anonymous'}
                        {t.updated_at && <> · updated {new Date(t.updated_at).toLocaleDateString()}</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setNoteFor(t)} data-testid={`add-note-btn-${t.id}`} title="Add internal note">
                        <MessageSquare className="w-4 h-4 text-blue-600" />
                      </Button>
                      {t.url && (
                        <Button size="sm" variant="ghost" asChild title="Open in portal">
                          <a href={t.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /></a>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Inline internal-note composer */}
        {noteFor && (
          <div className="border-t pt-3 -mx-6 px-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-slate-700">
                Internal note on #{noteFor.id} — <span className="text-slate-500">{noteFor.subject}</span>
              </p>
              <Button size="sm" variant="ghost" onClick={() => { setNoteFor(null); setNoteText(''); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Only your team will see this. Customers never see internal notes."
              rows={3}
              data-testid="internal-note-textarea"
            />
            <div className="flex justify-end mt-2">
              <Button onClick={submitNote} disabled={!noteText.trim() || posting} data-testid="post-internal-note-btn">
                {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                Post internal note
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Connect modal ─────────────────────────────────────────────────────────────

// iter132.1 — help panel showing the backend's outbound IP so admins can
// allowlist it on Jira/Zendesk/Freshdesk WAFs before hitting Test/Connect.
// Fetches once per dialog open; silently no-ops if the endpoint is missing.
const EgressIpNote = () => {
  const [state, setState] = useState({ loading: true, ips: [], error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/system/egress-ips');
        if (cancelled) return;
        setState({ loading: false, ips: r.data?.ips || [], error: r.data?.error || null });
      } catch (err) {
        if (!cancelled) setState({ loading: false, ips: [], error: 'unavailable' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const copyIp = async (ip) => {
    try {
      await navigator.clipboard.writeText(ip);
      toast.success(`Copied ${ip}`);
    } catch (_) { /* ignore */ }
  };

  if (state.loading) {
    return (
      <div className="mt-3 text-xs text-slate-500 flex items-center gap-1.5" data-testid="egress-ip-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Fetching our outbound IP…
      </div>
    );
  }

  if (!state.ips.length) {
    return (
      <div
        className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 flex items-start gap-1.5"
        data-testid="egress-ip-unavailable"
      >
        <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>
          Could not detect our outbound IP right now. If your portal enforces an IP allowlist, please
          contact support so we can share it manually.
        </span>
      </div>
    );
  }

  return (
    <div
      className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"
      data-testid="egress-ip-note"
    >
      <div className="flex items-start gap-1.5">
        <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold">If your portal has an IP allowlist:</p>
          <p className="mt-0.5">Allow this outbound IP so we can reach the ticket API:</p>
          <div className="mt-1.5 space-y-1">
            {state.ips.map((ip) => (
              <div key={ip} className="flex items-center gap-2" data-testid={`egress-ip-${ip}`}>
                <code className="px-1.5 py-0.5 rounded bg-white border border-amber-300 font-mono">{ip}</code>
                <button
                  type="button"
                  onClick={() => copyIp(ip)}
                  className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900"
                  aria-label={`Copy ${ip}`}
                  data-testid={`egress-ip-copy-${ip}`}
                >
                  <Copy className="w-3 h-3" /> copy
                </button>
              </div>
            ))}
          </div>
          <p className="mt-1.5 text-amber-700">This IP can rotate — reopen this dialog if you get WAF errors.</p>
        </div>
      </div>
    </div>
  );
};

const ConnectDialog = ({ chatbotId, provider, open, onClose, onConnected }) => {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok:boolean, message:string}

  useEffect(() => {
    if (open) {
      setForm({});
      setTestResult(null);
    }
  }, [open, provider]);

  if (!provider) return null;

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post(
        `/chatbots/${chatbotId}/support-portals/validate`,
        { provider: provider.key, ...form },
      );
      const info = r.data?.validated || {};
      const summary = info.instance || info.authed_as || provider.name;
      setTestResult({ ok: true, message: `Connection verified — ${summary}` });
      toast.success('Credentials look good');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Test failed';
      setTestResult({ ok: false, message: msg });
      // No toast — the inline banner is enough and toasts truncate long WAF errors
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/chatbots/${chatbotId}/support-portals`, { provider: provider.key, ...form });
      toast.success(`Connected to ${r.data?.validated?.instance || provider.name}`);
      onConnected(r.data?.connection);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not connect');
    } finally { setSubmitting(false); }
  };

  const allRequiredFilled = provider.fields
    .filter((f) => !f.label.toLowerCase().includes('optional'))
    .every((f) => (form[f.name] || '').trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${provider.color} flex items-center justify-center`}>
              <Headphones className="w-5 h-5 text-white" />
            </div>
            Connect {provider.name}
          </DialogTitle>
          <DialogDescription>{provider.description}</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 pt-2">
          {/* Step-by-step guide */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-sm font-semibold text-blue-900 mb-2">How to find your token:</p>
            <ol className="text-sm text-blue-800 space-y-1.5 list-decimal pl-4">
              {provider.helpSteps.map((step, idx) => (
                <li key={`step-${idx}`}>{step}</li>
              ))}
            </ol>
            <a
              href={provider.helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900 mt-3 font-medium"
            >
              Open {provider.name} docs <ExternalLink className="w-3 h-3" />
            </a>
            {/* iter132.1 — help note listing our egress IP so admins can allowlist it */}
            <EgressIpNote />
          </div>

          {/* Form fields */}
          <div className="space-y-3">
            {provider.fields.map((f) => (
              <div key={f.name}>
                <Label htmlFor={`field-${f.name}`} className="text-slate-700">{f.label}</Label>
                <Input
                  id={`field-${f.name}`}
                  type={f.type || 'text'}
                  placeholder={f.placeholder}
                  value={form[f.name] || ''}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  className="mt-1"
                  data-testid={`connect-field-${f.name}`}
                />
                {f.hint && <p className="text-xs text-slate-500 mt-1">{f.hint}</p>}
              </div>
            ))}
            {/* iter132.1 — inline pre-save test result banner */}
            {testResult && (
              <div
                className={`rounded-md p-2 text-xs flex items-start gap-1.5 ${
                  testResult.ok
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}
                data-testid={testResult.ok ? 'test-connection-success' : 'test-connection-error'}
              >
                {testResult.ok
                  ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  : <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />}
                <span className="whitespace-pre-wrap break-words">{testResult.message}</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="cancel-connection-btn">Cancel</Button>
          {/* iter132.1 — Test button pings validate() without persisting so admins get the
              fresh WAF/URL/token error INSIDE the dialog instead of blowing away their form. */}
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={!allRequiredFilled || testing || submitting}
            data-testid="test-connection-btn"
          >
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
            {testing ? 'Testing…' : 'Test connection'}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!allRequiredFilled || submitting}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="submit-connection-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            {submitting ? 'Verifying...' : 'Verify & Connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Connection row ───────────────────────────────────────────────────────────
const ConnectionRow = ({ chatbotId, conn, onChanged }) => {
  const provider = providerOf(conn.provider);
  const [testing, setTesting] = useState(false);
  const [showTickets, setShowTickets] = useState(false);
  // iter132.2 — inline test result banner. Toasts truncate long WAF
  // messages, so we surface the full humanized error next to the row.
  const [testBanner, setTestBanner] = useState(null); // {ok, message}

  const handleTest = async () => {
    setTesting(true);
    setTestBanner(null);
    try {
      const r = await api.post(`/chatbots/${chatbotId}/support-portals/${conn.connection_id}/test`);
      const info = r.data?.info || r.data?.validated || {};
      const summary = info.instance || info.authed_as || provider.name;
      setTestBanner({ ok: true, message: `Connection healthy — ${summary}` });
      toast.success('Connection healthy');
      onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Connection test failed';
      setTestBanner({ ok: false, message: msg });
      // Deliberately NO toast — the inline banner is the full-length surface
      onChanged();
    } finally { setTesting(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Disconnect ${conn.label}? AI Employee will lose access to its tickets.`)) return;
    try {
      await api.delete(`/chatbots/${chatbotId}/support-portals/${conn.connection_id}`);
      toast.success('Disconnected');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not disconnect');
    }
  };

  return (
    <>
      <div className="p-4 border border-slate-200 rounded-lg bg-white hover:bg-slate-50" data-testid={`portal-row-${conn.connection_id}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={`w-10 h-10 rounded-lg ${provider.color} flex items-center justify-center flex-shrink-0`}>
              <Headphones className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="font-medium text-slate-900 truncate">{conn.label}</p>
                {conn.last_test_ok === false ? (
                  <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Check creds</Badge>
                ) : (
                  <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Connected</Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 truncate">
                {provider.name}
                {conn.authed_as && <> · signed in as {conn.authed_as}</>}
              </p>
              {/* Persisted last-test error still shown for connections that failed on a
                  background retest — but suppress once the user runs a fresh test in this
                  session, since testBanner already tells the current story. */}
              {!testBanner && conn.last_test_error && (
                <p className="text-xs text-red-600 mt-0.5">{conn.last_test_error}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button size="sm" variant="outline" onClick={() => setShowTickets(true)} data-testid={`view-tickets-btn-${conn.connection_id}`}>
              <TicketIcon className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Tickets</span>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleTest}
              disabled={testing}
              title="Test connection"
              data-testid={`test-connection-row-btn-${conn.connection_id}`}
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
            <Button size="sm" variant="ghost" onClick={handleDelete} title="Disconnect" data-testid={`disconnect-btn-${conn.connection_id}`}>
              <Trash2 className="w-4 h-4 text-red-600" />
            </Button>
          </div>
        </div>
        {/* iter132.2 — full-width inline banner so 300-char WAF errors are
            readable without a truncating toast. Dismissible so the row can
            return to a clean state. */}
        {testBanner && (
          <div
            className={`mt-3 rounded-md p-2 text-xs flex items-start gap-1.5 ${
              testBanner.ok
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
            data-testid={testBanner.ok ? `test-row-success-${conn.connection_id}` : `test-row-error-${conn.connection_id}`}
          >
            {testBanner.ok
              ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              : <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />}
            <span className="flex-1 whitespace-pre-wrap break-words">{testBanner.message}</span>
            <button
              type="button"
              onClick={() => setTestBanner(null)}
              className={`flex-shrink-0 ${testBanner.ok ? 'text-emerald-700 hover:text-emerald-900' : 'text-red-700 hover:text-red-900'}`}
              aria-label="Dismiss"
              data-testid={`test-row-banner-close-${conn.connection_id}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      <TicketsViewer chatbotId={chatbotId} connection={conn} open={showTickets} onClose={() => setShowTickets(false)} />
    </>
  );
};

// ─── Main tab component ───────────────────────────────────────────────────────
export const SupportPortalsTab = ({ chatbotId }) => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectProvider, setConnectProvider] = useState(null);
  const [mode, setMode] = useState('read_only');
  const [updatingMode, setUpdatingMode] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/chatbots/${chatbotId}/support-portals`);
      setConnections(r.data?.connections || []);
      setMode(r.data?.mode || 'read_only');
    } catch (e) {
      // Empty state is fine
      setConnections([]);
    } finally { setLoading(false); }
  }, [chatbotId]);

  useEffect(() => { load(); }, [load]);

  const handleModeChange = async (newMode) => {
    setUpdatingMode(true);
    try {
      await api.patch(`/chatbots/${chatbotId}/support-portals/mode`, { mode: newMode });
      setMode(newMode);
      toast.success(newMode === 'autonomous'
        ? 'Autonomous mode ON — AI may now post internal notes on its own'
        : 'Switched to read-only mode');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update mode');
    } finally { setUpdatingMode(false); }
  };

  return (
    <div className="space-y-6" data-testid="support-portals-tab">
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <LifeBuoy className="w-5 h-5 text-blue-600" />
            Support Portal Integrations
          </CardTitle>
          <CardDescription>
            Let your AI Employee read open tickets and post internal notes for your team.
            Customers never see your internal notes — they&apos;re for your team&apos;s eyes only.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Provider picker / Add new */}
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            {PROVIDERS.map((p) => {
              const already = connections.find((c) => c.provider === p.key && c.last_test_ok !== false);
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setConnectProvider(p)}
                  className="text-left p-4 rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:shadow-md transition-all bg-white group"
                  data-testid={`connect-${p.key}-btn`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg ${p.color} flex items-center justify-center`}>
                      <Headphones className="w-5 h-5 text-white" />
                    </div>
                    <p className="font-semibold text-slate-900 group-hover:text-blue-700">{p.name}</p>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">{p.description}</p>
                  <div className="flex items-center gap-1.5">
                    {already ? (
                      <span className="text-xs text-emerald-600 font-medium inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />Add another
                      </span>
                    ) : (
                      <span className="text-xs text-blue-600 font-medium inline-flex items-center gap-1">
                        <Plus className="w-3 h-3" />Connect
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Connections list */}
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>
          ) : connections.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-slate-300 rounded-lg" data-testid="no-portals-empty-state">
              <LifeBuoy className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-1 font-medium">No portals connected yet</p>
              <p className="text-sm text-slate-500">Pick one above and follow the simple step-by-step guide.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {connections.map((c) => (
                <ConnectionRow key={c.connection_id} chatbotId={chatbotId} conn={c} onChanged={load} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mode toggle — Phase 2 */}
      <div className={`rounded-lg p-4 border ${mode === 'autonomous' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="support-portal-mode-banner">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className={`font-medium mb-1 flex items-center gap-2 ${mode === 'autonomous' ? 'text-emerald-900' : 'text-amber-900'}`}>
              {mode === 'autonomous' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {mode === 'autonomous' ? 'Autonomous mode' : 'Read-only mode'}
            </p>
            <p className={`text-sm ${mode === 'autonomous' ? 'text-emerald-800' : 'text-amber-800'}`}>
              {mode === 'autonomous' ? (
                <>Your AI Employee will <strong>read tickets and may post internal notes on its own</strong> (visible only to your team) when it picks up important context from a conversation. It still cannot reply to customers, assign tickets, or change ticket status on its own.</>
              ) : (
                <>Your AI Employee can <strong>read tickets</strong> and <strong>post internal notes when you ask it to</strong>. It will never act on its own. Switch to Autonomous when you trust how it behaves.</>
              )}
            </p>
          </div>
          <Button
            size="sm"
            variant={mode === 'autonomous' ? 'outline' : 'default'}
            onClick={() => handleModeChange(mode === 'autonomous' ? 'read_only' : 'autonomous')}
            disabled={updatingMode || connections.length === 0}
            className={mode === 'autonomous' ? '' : 'bg-emerald-600 hover:bg-emerald-700'}
            data-testid="toggle-portal-mode-btn"
          >
            {updatingMode ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'autonomous' ? 'Switch to Read-only' : 'Enable Autonomous')}
          </Button>
        </div>
        {connections.length === 0 && (
          <p className="text-xs text-slate-500 mt-2">Connect a portal first to enable Autonomous mode.</p>
        )}
      </div>

      <ConnectDialog
        chatbotId={chatbotId}
        provider={connectProvider}
        open={!!connectProvider}
        onClose={() => setConnectProvider(null)}
        onConnected={() => load()}
      />

      {/* Phase 3 — Approval queue for AI-drafted public replies */}
      {connections.length > 0 && (
        <PendingRepliesQueue chatbotId={chatbotId} mode={mode} />
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Pending Replies Queue (Phase 3)
//
// When the chatbot is in autonomous mode and the AI drafts a [public_reply] for
// a ticket, the draft is parked here. The owner can Approve / Edit / Reject it
// before it is actually posted to the customer.
// ──────────────────────────────────────────────────────────────────────────────
const PendingRepliesQueue = ({ chatbotId, mode }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/chatbots/${chatbotId}/support-portals/pending-replies`, {
        params: { status: filter },
      });
      setItems(r.data?.pending_replies || []);
    } catch (e) {
      setItems([]);
    } finally { setLoading(false); }
  }, [chatbotId, filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (item) => {
    setBusyId(item.pending_id);
    try {
      const editedBody = editing && editing.item.pending_id === item.pending_id
        ? editing.body
        : null;
      await api.post(
        `/chatbots/${chatbotId}/support-portals/pending-replies/${item.pending_id}/approve`,
        { edited_body: editedBody && editedBody !== item.draft_body ? editedBody : null }
      );
      toast.success('Reply posted to the customer');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not post reply');
    } finally { setBusyId(null); }
  };

  const reject = async (item) => {
    const reason = window.prompt('Why are you rejecting this draft? (optional)') || '';
    setBusyId(item.pending_id);
    try {
      await api.post(
        `/chatbots/${chatbotId}/support-portals/pending-replies/${item.pending_id}/reject`,
        { rejection_reason: reason }
      );
      toast.success('Draft rejected');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reject draft');
    } finally { setBusyId(null); }
  };

  const filters = [
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'failed', label: 'Failed' },
  ];

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="pending-replies-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-slate-900">
              <Inbox className="w-5 h-5 text-blue-600" />
              Approval Queue — Customer-Facing Replies
            </CardTitle>
            <CardDescription>
              When your AI is in Autonomous mode, drafts of public ticket replies land here for your approval before being sent.
              {mode !== 'autonomous' && (
                <span className="block mt-1 text-amber-700">
                  Currently in read-only mode — the AI won&apos;t draft public replies. Enable Autonomous above to start using the queue.
                </span>
              )}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="refresh-pending-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-1 mb-4 border-b border-slate-200">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                filter === f.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`pending-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-slate-300 rounded-lg">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-600 text-sm">No {filter} replies right now.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => {
              const isEditing = editing?.item.pending_id === it.pending_id;
              const isBusy = busyId === it.pending_id;
              return (
                <div
                  key={it.pending_id}
                  className="border border-slate-200 rounded-lg p-3 bg-slate-50"
                  data-testid={`pending-reply-${it.pending_id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 text-xs">
                      <Badge className="bg-indigo-100 text-indigo-700 capitalize">{it.provider}</Badge>
                      <span className="text-slate-700 font-medium">Ticket #{it.ticket_id}</span>
                      <span className="text-slate-400">{new Date(it.created_at).toLocaleString()}</span>
                    </div>
                    <StatusPill status={it.status} />
                  </div>
                  {it.ai_user_message && (
                    <details className="mb-2 text-xs">
                      <summary className="cursor-pointer text-slate-500 hover:text-slate-700">Customer message that triggered this draft</summary>
                      <p className="mt-1 text-slate-600 whitespace-pre-wrap bg-white p-2 rounded border border-slate-200">{it.ai_user_message}</p>
                    </details>
                  )}
                  {isEditing ? (
                    <Textarea
                      value={editing.body}
                      onChange={(e) => setEditing({ item: it, body: e.target.value })}
                      rows={6}
                      className="bg-white border-slate-300 text-sm"
                      data-testid={`edit-textarea-${it.pending_id}`}
                    />
                  ) : (
                    <div className="bg-white border border-slate-200 rounded p-3 text-sm text-slate-800 whitespace-pre-wrap">
                      {it.edited_body || it.draft_body}
                    </div>
                  )}
                  {it.status === 'pending' && (
                    <div className="flex items-center gap-2 mt-3">
                      {isEditing ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setEditing(null)} disabled={isBusy}>
                            Cancel edit
                          </Button>
                          <Button size="sm" onClick={() => approve(it)} disabled={isBusy} className="bg-emerald-600 hover:bg-emerald-700">
                            {isBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                            Approve edited reply
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" onClick={() => approve(it)} disabled={isBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${it.pending_id}`}>
                            {isBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                            Approve & Send
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditing({ item: it, body: it.draft_body })} disabled={isBusy} data-testid={`edit-${it.pending_id}`}>
                            <Edit3 className="w-4 h-4 mr-1" />
                            Edit first
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => reject(it)} disabled={isBusy} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`reject-${it.pending_id}`}>
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                  {it.status === 'failed' && it.delivery_result?.error && (
                    <p className="mt-2 text-xs text-red-600">Delivery error: {it.delivery_result.error}</p>
                  )}
                  {it.status === 'rejected' && it.rejection_reason && (
                    <p className="mt-2 text-xs text-slate-500">Reason: {it.rejection_reason}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-slate-200 text-slate-700',
    failed: 'bg-red-100 text-red-700',
  };
  return <Badge className={`capitalize ${map[status] || 'bg-slate-100 text-slate-700'}`}>{status}</Badge>;
};

export default SupportPortalsTab;
